import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Mail, RefreshCw, Loader2, CheckCircle } from 'lucide-react';
import { useTranslations } from '../context/TranslationsContext';
import { getLocalizedPath } from '../utils/routes';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PendingVerificationPage = () => {
  const { t: ti18n, i18n } = useTranslation();
  const { td: tCms } = useTranslations(['auth', 'common', 'errors']);
  const [searchParams] = useSearchParams();
  const lang = i18n.language;
  
  const email = searchParams.get('email') || '';
  
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState('');
  
  // Translation helper
  const t = (key, fallback = null) => {
    const cmsValue = tCms(key);
    if (cmsValue && cmsValue !== key) return cmsValue;
    const i18nVal = ti18n(key);
    if (i18nVal && i18nVal !== key) return i18nVal;
    return fallback || key;
  };

  const lp = (key) => getLocalizedPath(key, lang);

  const handleResend = async () => {
    if (!email) {
      setError(t('auth.emailRequired', 'Email jest wymagany'));
      return;
    }

    setResending(true);
    setError('');

    try {
      await axios.post(`${API}/auth/resend-verification`, { 
        email, 
        language: lang 
      });
      setResent(true);
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.alreadyVerified) {
        setError(t('auth.alreadyVerified', 'Ten email został już zweryfikowany'));
      } else {
        setError(errorData?.error || t('auth.resendFailed', 'Nie udało się wysłać emaila'));
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-20 px-6 flex items-center justify-center" data-testid="pending-verification-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="glass-card p-12 text-center">
          <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
            <Mail className="w-10 h-10 text-primary" />
          </div>
          
          <h1 className="text-2xl font-bold mb-4">
            {t('auth.checkYourEmail', 'Sprawdź swoją skrzynkę')}
          </h1>
          
          <p className="text-muted-foreground mb-2">
            {t('auth.verificationEmailSent', 'Wysłaliśmy email z linkiem weryfikacyjnym na adres:')}
          </p>
          
          {email && (
            <p className="text-primary font-semibold mb-6">
              {email}
            </p>
          )}
          
          <div className="bg-white/5 rounded-xl p-4 mb-6 text-left">
            <p className="text-sm text-muted-foreground">
              <strong>{t('auth.whatToDo', 'Co zrobić')}:</strong>
            </p>
            <ol className="text-sm text-muted-foreground mt-2 space-y-1 list-decimal list-inside">
              <li>{t('auth.step1CheckInbox', 'Otwórz swoją skrzynkę pocztową')}</li>
              <li>{t('auth.step2FindEmail', 'Znajdź email od AvePark')}</li>
              <li>{t('auth.step3ClickLink', 'Kliknij link "Potwierdź email"')}</li>
            </ol>
            <p className="text-xs text-muted-foreground/70 mt-3">
              {t('auth.checkSpam', 'Sprawdź również folder Spam jeśli nie widzisz wiadomości.')}
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
              {error}
            </div>
          )}

          {resent ? (
            <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-xl flex items-center justify-center gap-2 text-green-400">
              <CheckCircle className="w-5 h-5" />
              {t('auth.emailResent', 'Email został wysłany ponownie')}
            </div>
          ) : (
            <button
              onClick={handleResend}
              disabled={resending || !email}
              className="btn-secondary w-full mb-4 flex items-center justify-center gap-2"
              data-testid="resend-verification"
            >
              {resending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <RefreshCw className="w-5 h-5" />
              )}
              {resending 
                ? t('common.sending', 'Wysyłanie...') 
                : t('auth.resendVerification', 'Wyślij ponownie')}
            </button>
          )}

          <p className="text-sm text-muted-foreground">
            {t('auth.wrongEmail', 'Pomyłka w adresie email?')}{' '}
            <Link to={lp('register')} className="text-primary hover:underline">
              {t('auth.registerAgain', 'Zarejestruj się ponownie')}
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default PendingVerificationPage;
